import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useState } from 'react';
import { UserContext } from '../context/UserContext';

import { Breadcrumbs, Link as MUILink } from "@mui/material";
import PHDateTime from '../utils/PHDateTime';

import Swal from 'sweetalert2';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import PeopleIcon from '@mui/icons-material/People';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import InventoryIcon from '@mui/icons-material/Inventory';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import CategoryIcon from '@mui/icons-material/Category';
import PaymentIcon from '@mui/icons-material/Payment';
import CalculateIcon from '@mui/icons-material/Calculate';
import DescriptionIcon from '@mui/icons-material/Description';
import TaskIcon from '@mui/icons-material/Task';

const drawerWidth = 240;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    variants: [
        {
            props: ({ open }) => open,
            style: {
                marginLeft: drawerWidth,
                width: `calc(100% - ${drawerWidth}px)`,
                transition: theme.transitions.create(['width', 'margin'], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
        },
    ],
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    ...openedMixin(theme),
                    '& .MuiDrawer-paper': openedMixin(theme),
                },
            },
            {
                props: ({ open }) => !open,
                style: {
                    ...closedMixin(theme),
                    '& .MuiDrawer-paper': closedMixin(theme),
                },
            },
        ],
    }),
);

export default function MiniDrawer() {

    const apiUrl = process.env.REACT_APP_API_URL;
    const { session } = useContext(UserContext);
    const navigate = useNavigate();

    const theme = useTheme();
    const [open, setOpen] = useState(false);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const location = useLocation();
    const pathnames = location.pathname.split("/").filter((x) => x);

    const toSentenceCase = (str) => {
        return str
            .replace(/-/g, " ")
            .toLowerCase()
            .replace(/(^\w|\s\w)/g, (c) => c.toUpperCase());
    };

    const handleLogout = async () => {
        try {
            const response = await fetch(`${apiUrl}logout_user.php`, {
                method: "POST",
                credentials: "include",
            });

            const result = await response.json();

            if (result.status === "success") {
                navigate('/login');
            } else {
                Swal.fire({ title: "Error", text: result.message, icon: "error", });
            }
        } catch (error) {
            Swal.fire({ title: 'Server Error!', text: error.message, icon: 'error' });
            console.error("Logout error:", error);
        }
    };

    const logoutFunction = () => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will be logged out of your account.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, logout",
            cancelButtonText: "Cancel",
        }).then((result) => {
            if (result.isConfirmed) {

                handleLogout();

            }
        });
    };

    const listItemButtonStyle = [
        {
            minHeight: 48,
            px: 2.5,
        },
        open
            ? {
                justifyContent: 'initial',
            }
            : {
                justifyContent: 'center',
            },
    ]

    const listItemIconStyle = [
        {
            minWidth: 0,
            justifyContent: 'center',
        },
        open
            ? {
                mr: 3,
            }
            : {
                mr: 'auto',
            },
    ]

    const listItemTextStyle = [
        open
            ? {
                opacity: 1,
            }
            : {
                opacity: 0,
            },
    ]

    const menuItemsTop = [
        {
            text: "Dashboard",
            icon: <DashboardIcon />,
            path: "/",
            roles: ["Admin", "Manager", "CEO"],
        },
        {
            text: "Customer",
            icon: <PeopleIcon />,
            path: "/customer",
            roles: ["Admin", "Manager", "CEO"],
        },
        {
            text: "Orders",
            icon: <ShoppingCartRoundedIcon />,
            path: "/manage-orders",
            roles: ["Admin", "Manager", "CEO"],
        },
        {
            text: "Payment",
            icon: <CalculateIcon />,
            path: "/manage-payment",
            roles: ["Admin", "Manager", "CEO"],
        },
        {
            text: "List",
            icon: <DescriptionIcon />,
            path: "/order-list",
            roles: ["Admin", "Manager", "CEO"],
        },
        {
            text: "Arrived Items",
            icon: <TaskIcon />,
            path: "/manage-arrived",
            roles: ["Admin", "Manager", "CEO"],
        },
        {
            text: "Inventory",
            icon: <InventoryIcon />,
            path: "/manage-inventory",
            roles: ["Admin", "Manager", "CEO"],
        },
        {
            text: "Users",
            icon: <ManageAccountsIcon />,
            path: "/users",
            roles: ["Manager", "CEO"],
        },
        {
            text: "Categories",
            icon: <CategoryIcon />,
            path: "/categories",
            roles: ["Admin", "Manager", "CEO"],
        },
        {
            text: "Payment Methods",
            icon: <PaymentIcon />,
            path: "/payment-methods",
            roles: ["Manager", "CEO"],
        },
    ];

    const menuItemsBottom = [
        {
            text: 'Settings',
            icon: <SettingsIcon />,
            path: '/settings',
            roles: ["Manager", "CEO"],
        },
        {
            text: 'Logout',
            icon: <LogoutIcon />,
            action: logoutFunction,
            roles: ["Admin", "Manager", "CEO"],
        },
    ];

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar position="fixed" open={open}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={[
                            { mr: 2 },
                            open && { display: "none" },
                        ]}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography variant="h6" noWrap>
                        Bossbabe (OMS)
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    <PHDateTime />

                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                open={open}
                sx={{
                    '& .MuiDrawer-paper': {
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </DrawerHeader>

                <Divider />

                {/* Top List */}
                <List>
                    {menuItemsTop
                        .filter((item) => item.roles.includes(session.role))
                        .map((item) => {
                            const selected = location.pathname === item.path;

                            return (
                                <ListItem
                                    key={item.text}
                                    disablePadding
                                    sx={{ display: "block" }}
                                >
                                    <ListItemButton
                                        component={Link}
                                        to={item.path}
                                        sx={{
                                            ...listItemButtonStyle,
                                            backgroundColor: selected
                                                ? "#E3F2FD"
                                                : "transparent",
                                            borderLeft: selected
                                                ? "4px solid #1976D2"
                                                : "4px solid transparent",
                                            "&:hover": {
                                                backgroundColor: selected
                                                    ? "#E3F2FD"
                                                    : "#F5F5F5",
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                ...listItemIconStyle,
                                                color: selected ? "#1976D2" : "",
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>

                                        <ListItemText
                                            primary={item.text}
                                            sx={{
                                                ...listItemTextStyle,
                                                "& .MuiTypography-root": {
                                                    fontWeight: selected ? 700 : 500,
                                                    color: selected ? "#1976D2" : "",
                                                },
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                </List>

                {/* Bottom Section */}
                <Box sx={{ mt: 'auto' }}>
                    <Divider />
                    <List>
                        {menuItemsBottom
                            .filter((item) => item.roles.includes(session.role))
                            .map((item) => {
                                const selected = location.pathname === item.path;

                                return (
                                    <ListItem
                                        key={item.text}
                                        disablePadding
                                        sx={{ display: "block" }}
                                    >
                                        <ListItemButton
                                            component={Link}
                                            to={item.path}
                                            onClick={item.action}
                                            sx={{
                                                ...listItemButtonStyle,

                                                backgroundColor: selected
                                                    ? "#E3F2FD"
                                                    : "transparent",

                                                borderLeft: selected
                                                    ? "4px solid #1976D2"
                                                    : "4px solid transparent",

                                                "&:hover": {
                                                    backgroundColor: selected
                                                        ? "#E3F2FD"
                                                        : "#F5F5F5",
                                                },
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    ...listItemIconStyle,
                                                    color: selected && "#1976D2",
                                                }}
                                            >
                                                {item.icon}
                                            </ListItemIcon>

                                            <ListItemText
                                                primary={item.text}
                                                sx={{
                                                    ...listItemTextStyle,
                                                    "& .MuiTypography-root": {
                                                        fontWeight: selected ? 700 : 500,
                                                        color: selected && "#1976D2",
                                                    },
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                    </List>
                </Box>

            </Drawer>

            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <DrawerHeader />

                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                    <MUILink component={Link} underline="hover" color="inherit" to="/">
                        Dashboard
                    </MUILink>

                    {pathnames.map((value, index) => {
                        const to = "/" + pathnames.slice(0, index + 1).join("/");
                        const isLast = index === pathnames.length - 1;

                        return isLast ? (
                            <Typography color="text.primary" key={to}>
                                {value.replace("-", " ").toUpperCase()}
                            </Typography>
                        ) : (
                            <MUILink
                                component={Link}
                                underline="hover"
                                color="inherit"
                                to={to}
                                key={to}
                            >
                                {toSentenceCase(value)}
                            </MUILink>
                        );
                    })}
                </Breadcrumbs>

                <Outlet />
            </Box>
        </Box>
    );
}
